import { useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import R from 'ramda'

import {
  Box,
  Col,
  Icon,
  Columns,
  Heading,
  Image,
  LoadingOverlay,
  Loc,
  Text,
} from '@redriverpress/system'

import AutoScrollAlert from 'Common/components/alert/AutoScrollAlert'
import CheckboxField from 'Common/components/forms/CheckboxField'
import CleverIcon from 'Images/SSO_Clever.svg'
import Field from 'Common/components/forms/Field'
import GoogleIcon from 'Images/SSO_Google.svg'

import { Button, SSOButton, Divider } from '../../common/components/forms'
import { Link } from '../../common/components/typography'
import { useSignInContext } from '../context/sign-in'
import signIn from '../services/user'

const schema = Yup.object({
  login: Yup.string()
    .required('Email is required')
    .email('Invalid email'),
  password: Yup.string().required('Password is required'),
  remember_me: Yup.boolean(),
})

const defaultValues = {
  login: '',
  password: '',
  remember_me: false,
}

export default function SignInForm(props) {
  const {
    recoverable,
    rememberable,
    initialValues: contextValues,
    hiddenFields,
    googleSSOUrl,
    cleverSSOUrl,
    locale,
  } = useSignInContext()

  // Merge default values with context. In case both are present, use context
  const initialValues = R.mergeDeepRight(defaultValues, contextValues)

  // Use the default values as errors. If value is falsy then the field has an error.
  const initialErrors = initialValues

  const [isPending, setIsPending] = useState(false)
  const [alert, setAlert] = useState()

  const handleSubmit = formValues => {
    setIsPending(true)

    const user = R.merge(hiddenFields, formValues)
    user.login = user.login.toLowerCase()

    signIn({ user })
      .then(({ redirectTo }) => {
        window.location.href = redirectTo
      })
      .catch(err => {
        setAlert(err)
        setIsPending(false)
      })
  }

  return (
    <Box {...props}>
      {isPending && <LoadingOverlay />}

      <Heading as='h3' textAlign={[null, null, 'center']}>
        Welcome back!
      </Heading>
      {!!locale && (
        <Loc
          fontSize={[1, null, 2]}
          color='gray700'
          fontWeight='600'
          copy='Welcome back!'
        />
      )}

      <Columns mt={[3, null, null, 4]}>
        <Col width={[1, null, null, 1 / 2, 1]}>
          <SSOButton
            mb={[2, null, null, 0, 3]}
            width={1}
            as='a'
            href={googleSSOUrl}
            icon={<Image src={GoogleIcon} width='1.5rem' alt='Google logo' />}
          >
            Log In with Google
          </SSOButton>
        </Col>
        <Col width={[1, null, null, 1 / 2, 1]}>
          <SSOButton
            width={1}
            as='a'
            href={cleverSSOUrl}
            icon={<Image src={CleverIcon} width='1.5rem' alt='Clever logo' />}
          >
            Log In with Clever
          </SSOButton>
        </Col>
      </Columns>

      <Divider my={[3, null, null, null, 4]} />

      <Box mb={3}>
        <Heading as='h4'>Log in with your email</Heading>

        {alert && (
          <AutoScrollAlert
            alert={alert}
            fallback='Sign In could not be completed. Please check email and password and try again'
          />
        )}

        <Formik
          initialValues={initialValues}
          initialErrors={initialErrors}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isValid }) => (
            <Form>
              <Field
                type='email'
                name='login'
                label='Email'
                placeholder='Your email'
                icon='envelope'
              />
              <Field
                type='password'
                name='password'
                label='Password'
                placeholder='Your password'
                icon='key-skeleton'
              />
              <Box display='flex' alignItems='center' mb={4}>
                {rememberable && (
                  <CheckboxField
                    name='remember_me'
                    label='Remember Me'
                    width='auto'
                    mb={0}
                    small
                  />
                )}
                {recoverable && (
                  <Link ml='auto' href='/users/password/new' fontSize={0}>
                    Forgot password?
                  </Link>
                )}
              </Box>

              <Button type='submit' width={1} disabled={isPending || !isValid}>
                <Icon icon='sign-in' pr={2} />
                Log In
              </Button>
            </Form>
          )}
        </Formik>
      </Box>

      <Text textAlign={[null, null, null, 'center']} fontSize={0}>
        Not a member yet? <Link href='/sign_up_options'>Sign Up</Link>
      </Text>
    </Box>
  )
}
